import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useCallback, useEffect, useRef, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";
import { isIframed } from "../helpers/utils";

const MARKETINGSITE = process.env.REACT_APP_MARKETING_SITE_URL;

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useCloseModal = (show, history, parentPath) => {
  const prevShow = usePrevious(show);
  useEffect(() => {
    let hideModal;
    if (prevShow && !show) {
      hideModal = setTimeout(() => history.replace(parentPath), 150);
    }
    return () => {
      if (hideModal) clearTimeout(hideModal);
    };
  }, [prevShow, show, history, parentPath]);
  return;
};

export const usePageTitle = (title) => {
  useEffect(() => {
    let pageTitle = !isNil(title) && !isEmpty(title) ? title + " | " : "";
    document.title = `${pageTitle}LSO/Lone Star Overnight`;
  }, [title]);
};

export const useFieldFocus = () => {
  const fieldRef = useRef();
  useEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.focus();
    }
  }, []);

  return fieldRef;
};

export const useObserver = ({ callback, element }) => {
  const current = element && element.current;

  const observer = useRef(null);

  const observe = useCallback(() => {
    if (element && element.current && observer.current) {
      observer.current.observe(element.current);
    }
  }, [element]);

  useEffect(() => {
    // if we are already observing old element
    const currentEl = element.current;
    if (observer && observer.current && current) {
      observer.current.unobserve(current);
    }
    const resizeObserverOrPolyfill = ResizeObserver;
    observer.current = new resizeObserverOrPolyfill(callback);
    observe();

    return () => {
      if (observer && observer.current && element && currentEl) {
        observer.current.unobserve(currentEl);
      }
    };
  }, [current, callback, element, observe]);
};

// Send message to parent when DOM height changes
export const useAutoIframeHeight = (
  elementRef,
  msgProperty = "frameHeight"
) => {
  const getSize = (entries) => {
    let rect = entries[0].contentRect;
    if (isIframed) {
      // localStorage.setItem([msgProperty], JSON.stringify(rect.height));
      window.parent.postMessage(
        {
          [msgProperty]: rect.height,
        },
        MARKETINGSITE
      );
    }
  };
  useObserver({
    callback: getSize,
    element: elementRef,
  });
};

export const useResize = (element = null) => {
  let [{ screenWidth, screenHeight, ratiowh, ratiohw, rect }, setState] =
    useState({
      screenWidth: 0,
      screenHeight: 0,
      ratiowh: 0,
      ratiohw: 0,
      rect: undefined,
    });

  const onResize = (event) => {
    screenWidth = window.innerWidth;
    screenHeight = window.innerHeight;
    ratiowh = screenWidth / screenHeight;
    ratiohw = screenHeight / screenWidth;

    if (element && element.current) {
      //rect = element.current.getBoundingClientRect();
      const clientRect = element.current.getBoundingClientRect();
      // DOM API does not allow for a shallow copy, so we have to manually set them
      rect = {
        width: clientRect.width,
        height: clientRect.height,
        left: clientRect.left,
        right: clientRect.right,
        top: clientRect.top,
        bottom: clientRect.bottom,
      };
    }

    setState({ screenWidth, screenHeight, ratiowh, ratiohw, rect });
  };

  useEffect(() => {
    window.addEventListener("resize", onResize, false);
    window.onload = () => onResize();
    return () => {
      window.removeEventListener("resize", onResize, false);
    };
    // [] ==> let only resize:event handle state update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { screenWidth, screenHeight, ratiowh, ratiohw, rect };
};

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useEventCallback = (fn) => {
  const ref = useRef(() => {
    throw new Error("Cannot call an event handler while rendering.");
  });

  useEffect(() => {
    ref.current = fn;
  }, [fn]);

  return useCallback((params) => {
    const fn = ref.current;
    return fn(params);
  }, [ref]);
};
